// @ts-check
import './plot-map-app.css';
import { h } from 'preact';
import { useState, useCallback, useEffect } from 'preact/hooks';

import useMultipleAdminAjax from '../../hooks/useMultipleAdminAjax';
import useIntersect from '../../utils/useIntersect';
import useWindowSize from '../../utils/useWindowSize';

import { getShapesForSvgMap, getPlotNumberForModal, getPlotNumberIdFromShapeLink } from '../../utils/plotNumberHelpers';
import { getFiltersForModal, numberOfSetFilters, getUpdatedValue, isMatch, getMatchCountForFilterValue, getMatchCountForRangeFilterValue } from '../../utils/filterHelpers';
import { sortAndFilterDefaultStatuses, areThereDefaultHighLightedPlotsOnTheActiveView } from '../../utils/statusLegendHelpers';
import { getShapeById } from '../../utils/svgMapHelpers';

// components
import MapViewport from '../MapViewport';
import SvgMap from '../SvgMap';
import PlotDetailModal from '../PlotDetailModal/PlotDetailModal';
import HeaderNav from '../HeaderNav';
import ViewNavModal from '../ViewNavModal/ViewNavModal';
import StatusLegend from '../StatusLegend/StatusLegend';
import FilterButton from '../FilterButton/FilterButton';
import OnBoardingModal from '../OnBoardingModal';
import FilterModal from '../FilterModal/FilterModal';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';
import Message from '../Message/Message';
import 'react-photoswipe-2/lib/photoswipe.css';
import { PhotoSwipe } from 'react-photoswipe-2';

const { __, sprintf } = wp.i18n;

// TODO: move utilfunctions?
const isInView = (entry) => entry.intersectionRatio > 1;
const isUnFocused = (entry) => entry.intersectionRatio < 0.5;
// TODO: height of 10rem (10x16) is also defined in css (not dry)
const getMapSize = (windowSize, viewSize, isHeaderActive, isFooterActive) => {
	let additionalHeight = 0;
	additionalHeight += isHeaderActive ? 5*16 : 0;
	additionalHeight += isFooterActive ? 5*16 : 0;
	return {
		width: (windowSize.height-additionalHeight)/viewSize.height*viewSize.width,
		height: windowSize.height-additionalHeight
	};
};

const isHeaderActive = (isFiltersActive, numberOfViews) => isFiltersActive || numberOfViews > 1;
const isFooterActive = (isFiltersActive, numberOfStatuses) => !isFiltersActive && numberOfStatuses > 0;

// TODO: refactor into smaller pieces
/**
 * @param {object} props
 * @param {string} props.id
 * @param {string} props.placeholderBackgroundImageSrc
 * @param {boolean} props.displayLotNumbers
 * @param {string} props.fetchGroupsUrl
 * @param {string} props.fetchPlotNumbersUrl
 * @param {string|null} props.fetchFiltersUrl
 * @param {object} props.filterOnBoarding
 * @param {import('./types').PlotProperty[]} props.informationToShowForAPlot
 * @param {object} props.registerButtonLink
 * @param {import('./types').PlotStatusStatus[]} props.statusLegendDefaults
 * @returns
 */
export default function PlotMapApp({
	id,
	placeholderBackgroundImageSrc,
	displayLotNumbers = false,
	fetchGroupsUrl,
	fetchPlotNumbersUrl,
	fetchFiltersUrl,
	filterOnBoarding,
	informationToShowForAPlot,
	registerButtonLink,
	statusLegendDefaults
}) {
	const [[fetchedPlotNumbers, fetchedGroups, fetchedFilters], { loading, error }] = useMultipleAdminAjax({ fetchUrls: [fetchPlotNumbersUrl, fetchGroupsUrl, fetchFiltersUrl] });
	const [filters, setFilters] = useState([]);
	const [statuses, setStatuses] = useState([]);
	const [plotNumbers, setPlotNumbers] = useState([]);
	const [filterValues, setFilterValues] = useState([]);
	const [activeGroup, setActiveGroup] = useState(null);
	const [activeView, setActiveView] = useState(null);
	const [plotNumberForModal, setPlotNumberForModal] = useState(null);
	const [selectedPlotNumber, setSelectedPlotNumber] = useState(null);
	const [galleryImages, setGalleryImages] = useState([]);
	const [isPlotDetailModalOpen, setPlotDetailModalOpenStatus] = useState(false);
	const [isFullScreenImageGalleryOpen, setFullScreenImageGalleryOpenStatus] = useState(false);
	const [fullScreenImageGalleryIndex, setFullScreenImageGalleryIndex] = useState(0);
	const [isFilterModalOpen, setFilterModalOpenStatus] = useState(false);
	const [isFilterOnBoardingModalOpen, setFilterOnBoardingModalOpenStatus] = useState(true);
	const [setRefLoadedContainer, entry, refLoadedContainer] = useIntersect({ threshold: [1, 0.5] });
	const [mapViewportInitialScrollLeft, setMapViewportInitialScrollLeft] = useState(0);
	const windowSize = useWindowSize();

	const isFiltersActive = Boolean(fetchFiltersUrl);

	useEffect(() => {
		if (isUnFocused(entry)) {
			setSelectedPlotNumber(null);
			setPlotDetailModalOpenStatus(false);
			setFilterModalOpenStatus(false);
		}
	}, [entry]);

	useEffect(() => {
		if (Array.isArray(fetchedGroups) && fetchedGroups.length > 0) {
			// NOTE: show all statuses as selected when there is no single plot number with the "available" status on the current active view (for ux purposes)
			// TODO: this assumes the PlotAppMap component knows the content of the statusLegendDefaults prop
			const showAllStatuses = isFiltersActive || !areThereDefaultHighLightedPlotsOnTheActiveView(fetchedPlotNumbers, fetchedGroups[0]?.views[0], statusLegendDefaults);
			setStatuses(sortAndFilterDefaultStatuses(fetchedPlotNumbers.map(plotNumber => ({
				...plotNumber.status,
				isSelected: showAllStatuses || statusLegendDefaults.filter(status => status.isSelected).map(status => status.value).indexOf(plotNumber.status.value) >= 0
			})), statusLegendDefaults));
		}
	}, [fetchedGroups]);

	useEffect(() => {
		if (Array.isArray(fetchedGroups) && fetchedGroups.length > 0 && plotNumbers.length > 0) {
			const foundGroups = fetchedGroups.filter(group => group.level === 0);

			if (foundGroups.length !== 1) {
				return;
			}

			setActiveGroup({
				...foundGroups[0],
				views: foundGroups[0].views.map(view => ({
					...view,
					shapes: getShapesForSvgMap(view.shapes, plotNumbers, selectedPlotNumber ? selectedPlotNumber.id : null, statuses)
				}))
			});
		}
	}, [fetchedGroups, plotNumbers, selectedPlotNumber]);

	useEffect(() => {
		if (!activeGroup) {
			return;
		}

		if (activeGroup.views.length <= 0) {
			return;
		}

		// Update the activeView when it is in de current group, otherwise default to first view in current group
		setActiveView((prevActiveView) => activeGroup.views.find(view => view.id === prevActiveView?.id) || activeGroup.views[0]);
	}, [activeGroup]);

	useEffect(() => {
		if (Array.isArray(fetchedPlotNumbers)) {
			setPlotNumbers(fetchedPlotNumbers);
		}
	}, [fetchedPlotNumbers]);

	useEffect(() => {
		if (!fetchedFilters || !fetchedPlotNumbers) {
			return;
		}

		const emptyFilterValuesState = fetchedFilters.map(filter => ({
			name: filter.name,
			value: null
		}));

		setFilters(fetchedFilters.map(filter => ({
			...filter,
			choices: filter.choices.filter(choice => filter.type === 'range' ?
				getMatchCountForRangeFilterValue(fetchedPlotNumbers, emptyFilterValuesState, filter.name, choice.value) :
				getMatchCountForFilterValue(fetchedPlotNumbers, emptyFilterValuesState, filter.name, filter.type === 'checkbox' ? [choice.value] : choice.value))
		})));
	}, [fetchedFilters, fetchedPlotNumbers]);

	useEffect(() => {
		if (filters.length === 0) {
			return;
		}

		setFilterValues(filters.map(filter => ({
			name: filter.name,
			value: filter.defaultValue ? filter.choices.filter(choice => filter.defaultValue.indexOf(choice.value) >= 0).map(choice => choice.value) : null
		})));
	}, [filters]);

	useEffect(() => {
		if (!Array.isArray(fetchedPlotNumbers) || filters.length === 0) {
			return;
		}
		setPlotNumbers(fetchedPlotNumbers.filter(plotNumber => filterValues.filter(filter => isMatch(filter.name, plotNumber, filter.value)).length === filterValues.length));
	}, [filterValues]);

	const handleStatusLegendChange = useCallback(value => {
		setStatuses(statuses.map(status => ({
			...status,
			isSelected: value === status.value ? !status.isSelected : status.isSelected
		})));
	  }, [statuses]);

	const handlePlotClick = useCallback(id => {
		const shape = getShapeById(id, activeGroup);

		if (!shape) {
			return;
		}

		const plotNumberId = getPlotNumberIdFromShapeLink(shape.link);

		if (!isInView(entry) && refLoadedContainer && refLoadedContainer.nodeType === 1) {
			refLoadedContainer.scrollIntoView();
		}

		const updatedPlotNumbers = plotNumbers.map(plotNumber => ({
			...plotNumber,
			isSelected: plotNumberId === plotNumber.id
		}));

		const selectedPlotNumbers = updatedPlotNumbers.filter(plotNumber => plotNumber.isSelected);
		setPlotNumberForModal(selectedPlotNumbers.length === 1 ? selectedPlotNumbers[0] : null);
		setSelectedPlotNumber(selectedPlotNumbers.length === 1 ? selectedPlotNumbers[0] : null);
		setFullScreenImageGalleryIndex(0);

		setPlotDetailModalOpenStatus(true);
		setFilterModalOpenStatus(false);
	}, [plotNumbers, refLoadedContainer, entry, activeGroup]);

	const handleOutsidePlotClick = useCallback(() => {
		setPlotDetailModalOpenStatus(false);
		setSelectedPlotNumber(null);
	}, [plotNumbers]);

	const handleClosePlotNumberModal = useCallback(() => {
		setSelectedPlotNumber(null);
		setPlotDetailModalOpenStatus(false);
	}, []);

	const handleOpenFilterModal = useCallback(() => {
		setFilterOnBoardingModalOpenStatus(false);
		setFilterModalOpenStatus(true);
		setSelectedPlotNumber(null);
		setPlotDetailModalOpenStatus(false);
	}, []);

	const handleFilterChange = useCallback((name, value) => {
		setFilterValues(filterValues.map(filter => ({
			...filter,
			value: filter.name === name ? getUpdatedValue(name, value, filter.value) : filter.value
		})));
	}, [filterValues]);

	const handleFilterReset = useCallback(name => {
		setFilterValues(filterValues.map(filter => ({
			...filter,
			value: filter.name === name ? null : filter.value
		})));
	}, [filterValues]);

	const handleFiltersReset = useCallback(() => {
		setFilterValues(filterValues.map(filter => ({
			...filter,
			value: null
		})));
	}, [filterValues]);

	const handleCloseFilterModal = useCallback(() => {
		setFilterModalOpenStatus(false);
	}, []);

	const handleCloseFilterOnboardingModal = useCallback(() => {
		setFilterOnBoardingModalOpenStatus(false);
	}, []);

	const getMatchCountForFilter = useCallback((name, value) => (
		fetchedPlotNumbers.filter(plotNumber => filterValues.filter(filter => isMatch(filter.name, plotNumber, filter.name === name ? value : filter.value)).length === filterValues.length).length
	), [fetchedPlotNumbers, filterValues]);

	const handleOpenFullScreenImageGallery = useCallback((openIndex, images) => {
		// TODO: fallback for when there are no width and height known for all images (Nieuwbouw Nederland source has this problem), remove when not needed anymore
		if (images.filter(image => image.width && image.height).length !== images.length) {
			images = images.map(image => {
				const imageElement = document.querySelector('.image-gallery-image-id-' + image.id + ' > img');
				return {
					...image,
					width: imageElement ? imageElement.naturalWidth : image.width,
					height: imageElement ? imageElement.naturalHeight : image.height
				};
			});
		}

		setGalleryImages(images);
		setFullScreenImageGalleryIndex(openIndex);
		setFullScreenImageGalleryOpenStatus(true);
	}, []);

	const handleCloseFullScreenImageGallery = useCallback(() => {
		setFullScreenImageGalleryOpenStatus(false);
	}, []);

	const handleSwitchView = useCallback((id) => {
		const foundViews = activeGroup.views.filter(view => view.id === id);

		setActiveView(foundViews.length === 1 ? foundViews[0] : null);
		setSelectedPlotNumber(null);
		setPlotDetailModalOpenStatus(false);
	}, [activeGroup]);

	const handleSvgBoundingBoxChange = useCallback((SvgRect) => {
		if (!activeView) {
			return;
		}

		const mapSize = getMapSize(windowSize, activeView.backgroundImage, isHeaderActive(isFiltersActive, activeGroup?.views.length), isFooterActive(isFiltersActive, statuses.length));

		if (mapSize.width < windowSize.width) {
			return;
		}

		const mapSizeScaleFactor = mapSize.width / activeView.backgroundImage.width;
		const correctedBoundingBoxCenterX = (SvgRect.width / 2 + SvgRect.x) * mapSizeScaleFactor;

		setMapViewportInitialScrollLeft(correctedBoundingBoxCenterX - (windowSize.width / 2));
	}, [windowSize, activeView]);

	useEffect(() => {
		if (windowSize.width < 512 && (isPlotDetailModalOpen || isFilterModalOpen)) {
			document.body.classList.add('plot-map-no-scroll');
		}
		else {
			document.body.classList.remove('plot-map-no-scroll');
		}
	}, [isPlotDetailModalOpen, isFilterModalOpen, windowSize.width]);

	// TODO: more clear to refactor out the if else statements
	// TODO: FilterModal refactor into children?
	return (
		loading ? <div class="plot-map-app-container">
			<div style={`background-image: url('${placeholderBackgroundImageSrc}');`} class="plot-map-placeholder-image" />
			<LoadingSpinner />
		</div> :
			!loading && !fetchedPlotNumbers?.length > 0 ? <div class="plot-map-app-container contains-message">
				<div style={`background-image: url('${placeholderBackgroundImageSrc}');`} class="plot-map-placeholder-image" />
				{error && <Message type="error" message={error.message} />}
				{!error && <Message message={__('No plot numbers found', 'real-estate-project')} />}
			</div> :
				<div
					ref={setRefLoadedContainer}
					class={'plot-map-app-container' + (isInView(entry) ? ' is-in-view' : '') + (isHeaderActive(isFiltersActive, activeGroup?.views.length) ? ' contains-header' : '') + (isFooterActive(isFiltersActive, statuses.length) ? ' contains-footer' : '')}
				>
					{isHeaderActive(isFiltersActive, activeGroup?.views.length) &&
						<HeaderNav>
							{isFiltersActive && filters.length > 0 &&
								<FilterButton
									onClickHandler={handleOpenFilterModal}
									numberOfFiltersActive={numberOfSetFilters(filterValues)}
								/>}
							{activeGroup?.views.length > 1 && activeView &&
								<ViewNavModal
									activeView={activeView}
									views={activeGroup.views}
									onClick={handleSwitchView}
									windowSize={windowSize}
								/>}
							{isFiltersActive && filterOnBoarding && <OnBoardingModal isOpen={isFilterOnBoardingModalOpen} onClose={handleCloseFilterOnboardingModal}>
								<h4>{filterOnBoarding.title}</h4>
								<p>{filterOnBoarding.text}</p>
								<p><button onClick={handleOpenFilterModal} type="button" className="button">
									{getMatchCountForFilter() === 1 ?
										sprintf(__('Filter %d result', 'real-estate-project'), getMatchCountForFilter()) :
										sprintf(__('Filter %d results', 'real-estate-project'), getMatchCountForFilter())}
								</button></p>
							</OnBoardingModal>}
						</HeaderNav>}
					{activeView &&
						<MapViewport
							windowSize={windowSize}
							mapSize={getMapSize(windowSize, activeView.backgroundImage, isHeaderActive(isFiltersActive, activeGroup?.views.length), isFooterActive(isFiltersActive, statuses.length))}
							handleClick={handleOutsidePlotClick}
							initialScrollLeft={mapViewportInitialScrollLeft}
						>
							<SvgMap
								shapes={activeView.shapes}
								plotNumbers={plotNumbers}
								svgSize={activeView.backgroundImage}
								mapSize={getMapSize(windowSize, activeView.backgroundImage, isHeaderActive(isFiltersActive, activeGroup?.views.length), isFooterActive(isFiltersActive, statuses.length))}
								backgroundImageSrc={activeView.backgroundImage.src || placeholderBackgroundImageSrc}
								handleShapeClick={handlePlotClick}
								onSvgBoundingBoxChange={handleSvgBoundingBoxChange}
								displayLotNumbers={displayLotNumbers}
							/>
						</MapViewport>}
					{isFooterActive(isFiltersActive, statuses.length) ?
						<StatusLegend statuses={statuses} mapId={id} handleChange={handleStatusLegendChange} /> : ''}
					<PlotDetailModal
						{...getPlotNumberForModal(plotNumberForModal, informationToShowForAPlot)}
						registerLink={registerButtonLink}
						isActive={isPlotDetailModalOpen}
						informationToShow={informationToShowForAPlot}
						onCloseHandler={handleClosePlotNumberModal}
						onFullScreenImageGalleryOpen={handleOpenFullScreenImageGallery}
					/>
					{filters.length > 0 ?
						<FilterModal
							mapId={id}
							filters={getFiltersForModal(filters, filterValues)}
							filterValues={filterValues}
							numberOfResults={plotNumbers.length}
							numberOfFiltersActive={numberOfSetFilters(filterValues)}
							isActive={isFilterModalOpen}
							onFilterChangeHandler={handleFilterChange}
							onFilterResetHandler={handleFilterReset}
							onFiltersResetHandler={handleFiltersReset}
							onCloseHandler={handleCloseFilterModal}
							getMatchCountForFilter={getMatchCountForFilter}
						/> : ''}
					<PhotoSwipe
						isOpen={isPlotDetailModalOpen && isFullScreenImageGalleryOpen}
						options={{
							shareEl: false,
							bgOpacity: 0.8,
							index: fullScreenImageGalleryIndex,
							history: false
						}}
						items={galleryImages.map(image => ({
							...image,
							thumbnail: image.src,
							w: image.width,
							h: image.height
						}))} onClose={handleCloseFullScreenImageGallery}
					/>
				</div>
	);
}
